import { useState } from 'react';

import { Check, Plus, Times } from '../icons.js';

import { useAutosave } from './autosave.js';
import { checklistStats, lastOrderKey } from './checklist.js';

import type { ChecklistItem } from '@nedelnik/shared';

/**
 * Чек-лист задачи (3.7).
 *
 * Появляется, только когда пункты есть (3.7.1) — кроме строки добавления, без
 * которой их неоткуда взять. Пункты отмечаются, правятся и удаляются по одному
 * (3.7.2): каждый из них — отдельная запись, поэтому правка одного не трогает
 * остальные.
 */
export function Checklist({
  items,
  onAdd,
  onToggle,
  onText,
  onRemove,
}: {
  items: readonly ChecklistItem[];
  onAdd: (text: string, afterOrderKey: string | null) => void;
  onToggle: (item: ChecklistItem) => void;
  onText: (item: ChecklistItem, text: string) => void;
  onRemove: (item: ChecklistItem) => void;
}): React.ReactElement {
  const [text, setText] = useState('');
  const stats = checklistStats(items);

  function submit(): void {
    if (text.trim().length === 0) return;
    onAdd(text, lastOrderKey(items));
    // Поле остаётся готовым к следующему пункту: пункты вводятся подряд.
    setText('');
  }

  return (
    <div className="field">
      <div className="field__label">
        Чек-лист
        {stats.total > 0 && (
          <span className="field__count">
            {stats.done}/{stats.total}
          </span>
        )}
      </div>

      {stats.total > 0 && <ChecklistProgress done={stats.done} total={stats.total} />}

      <ul className="cl">
        {items.map((item) => (
          <ChecklistRow
            key={item.id}
            item={item}
            onToggle={() => onToggle(item)}
            onText={(next) => onText(item, next)}
            onRemove={() => onRemove(item)}
          />
        ))}
      </ul>

      {/*
        Кнопка рядом с полем обязательна: на мобильной клавиатуре Enter до поля
        доходит не всегда, и это общее правило для всего, что добавляется
        подряд (3.3.4).
      */}
      <form
        className="cl__add"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          aria-label="Новый пункт чек-листа"
          placeholder="Добавить пункт"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button type="submit" className="miniadd" aria-label="Добавить пункт">
          <Plus />
        </button>
      </form>
    </div>
  );
}

/** Полоска прогресса: то же число, что и `2/3`, но видное боковым зрением. */
export function ChecklistProgress({
  done,
  total,
}: {
  done: number;
  total: number;
}): React.ReactElement {
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-label={`Чек-лист: ${String(done)} из ${String(total)}`}
    >
      <i style={{ width: `${String(percent)}%` }} />
    </div>
  );
}

/**
 * Высота поля по содержимому.
 *
 * `field-sizing: content` сделал бы это одной строкой CSS, но его понимают не
 * все браузеры, до которых дотягивается приложение, — а пункт, обрезанный
 * ровно там, где браузер постарше, хуже трёх строк кода.
 */
function resize(field: HTMLTextAreaElement): void {
  field.style.height = 'auto';
  field.style.height = `${String(field.scrollHeight)}px`;
}

/** То же при первой отрисовке: приехавший с сервера пункт уже может быть длинным. */
function grow(field: HTMLTextAreaElement | null): void {
  if (field !== null) resize(field);
}

function ChecklistRow({
  item,
  onToggle,
  onText,
  onRemove,
}: {
  item: ChecklistItem;
  onToggle: () => void;
  onText: (text: string) => void;
  onRemove: () => void;
}): React.ReactElement {
  const { draft, change, flush } = useAutosave(item.text ?? '', onText);

  return (
    <li className={`cli${item.done ? ' cli--done' : ''}`}>
      {/*
        Многострочное поле, а не однострочное: пункт — это содержимое, а не
        подсказка, и видеть его надо целиком. В однострочном длинный пункт
        обрывался на середине («спросить про направление к аллерг…»), и прочесть
        его можно было, только ткнув внутрь и проскроллив.

        Enter при этом по-прежнему заканчивает правку, а не переносит строку:
        пункт чек-листа — одна мысль, абзацев в нём не бывает.
      */}
      <textarea
        ref={grow}
        className="cli__text"
        aria-label="Пункт чек-листа"
        rows={1}
        value={draft}
        onChange={(event) => {
          change(event.target.value);
          resize(event.currentTarget);
        }}
        onBlur={flush}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            flush();
            event.currentTarget.blur();
          }
        }}
      />
      {/*
        Отметка справа от текста, как у задачи в списке: слева она задавала
        строке рваный левый край, а собственного смысла у этого места нет.
        Порядок тот же, что в виджете задачи, — сначала отметка, потом
        удаление у самого края.
      */}
      <button
        type="button"
        className="iconbtn check"
        aria-label={item.done ? 'Снять отметку' : 'Пункт выполнен'}
        aria-pressed={item.done}
        onClick={onToggle}
      >
        <Check />
      </button>
      <button
        type="button"
        className="iconbtn iconbtn--danger"
        aria-label="Удалить пункт"
        onClick={onRemove}
      >
        <Times />
      </button>
    </li>
  );
}
